import {
  Component,
  OnInit,
  AfterViewInit,
  OnDestroy,
  SecurityContext,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { DomSanitizer } from '@angular/platform-browser';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

interface CameraState {
  url: string;
  isLoading: boolean;
  hasError: boolean;
  isLoaded: boolean;
}

/**
 * Displays one or more public live camera streams based on camera ID(s) provided in the route.
 * Renders sanitized iframe(s) for each camera, tracks per-iframe loading state, and shows a simple error state when streams fail or time out.
 * @component
 * @selector app-public-stream
 * @example
 *   <!-- Routed example: navigate to a route that provides a comma-separated list of camera IDs in the "id" param, e.g. /public-stream/abc,def -->
 *   <app-public-stream></app-public-stream>
 * @input {string} id - Camera ID or comma-separated list of camera IDs supplied via the ActivatedRoute param named "id" (not an Angular @Input property).
 * @output {none} none - This component does not emit any Angular @Output events; it manages internal UI state only.
 *
 * Key properties:
 * - cameraIds: string[] - Parsed array of camera IDs extracted from the route parameter.
 * - cameras: CameraState[] - Per-camera state objects containing a sanitized iframe URL and flags: isLoading, hasError, isLoaded.
 * - error: string | null - Stores a human-readable error when no valid camera IDs are provided or parsing fails.
 * - IFRAME_TIMEOUT: number - Timeout in milliseconds used to mark an iframe load as failed (set to 15000 ms).
 *
 * Key methods:
 * - setupIframeLoadTracking(): void - Locates iframe elements rendered in the DOM, attaches onload and onerror handlers, and creates per-iframe timeouts. Updates the corresponding CameraState flags (isLoading, isLoaded, hasError) based on successful loads, errors, or timeouts.
 *
 * Important lifecycle hooks:
 * - ngOnInit(): Subscribes to ActivatedRoute.paramMap, reads the "id" route parameter (supports comma-separated IDs), initializes cameraIds and the cameras array with sanitized stream URLs, and sets initial loading state.
 * - ngAfterViewInit(): Defers and invokes setupIframeLoadTracking() after the view initializes to attach event handlers to the actual iframe DOM elements.
 *
 * Behavior notes:
 * - Stream URLs are sanitized using DomSanitizer.bypassSecurityTrustResourceUrl before being bound to iframe src attributes to prevent security issues.
 * - The component is standalone and expects camera identifiers to come from routing rather than inputs; if no valid IDs are present, it sets an appropriate error message for the UI.
 */
@Component({
  selector: 'app-public-stream',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './public-stream.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./public-stream.component.scss'],
})
export class PublicStreamComponent implements OnInit, AfterViewInit, OnDestroy {
  cameraIds: string[] = [];
  cameras: CameraState[] = [];
  error: string | null = null;
  private readonly IFRAME_TIMEOUT = 15000;
  private destroy$ = new Subject<void>();

  constructor(
    private route: ActivatedRoute,
    private sanitizer: DomSanitizer
  ) {}

  /**
   * Initialize component by reading camera ID(s) from the route, building sanitized streaming URLs, and preparing camera loading state.
   * @example
   * gOnInit()
   * undefined
   * @returns {void} No return value.
   **/
  ngOnInit(): void {
    // Get camera ID(s) from route parameter
    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const idParam = params.get('id');

      if (idParam) {
        // Split comma-separated IDs or use single ID
        this.cameraIds = idParam.split(',').filter(id => id.trim().length > 0);

        if (this.cameraIds.length > 0) {
          // Initialize camera states immediately - show content right away
          this.cameras = this.cameraIds.map(cameraId => {
            const streamingUrl = `https://console.rhombussystems.com/share/live/${cameraId.trim()}`;
            const sanitizedUrl = this.sanitizer.sanitize(SecurityContext.URL, streamingUrl) ?? '';
            return {
              url: sanitizedUrl,
              isLoading: true,
              hasError: false,
              isLoaded: false,
            };
          });
        } else {
          this.error = 'Invalid camera ID in URL';
        }
      } else {
        this.error = 'Camera ID not found in URL';
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit(): void {
    // Set up iframe load tracking after view initializes
    setTimeout(() => {
      this.setupIframeLoadTracking();
    }, 100);
  }

  /**
   * Initialize and attach load/error tracking to all .stream-iframe elements, updating corresponding this.cameras entries (isLoading, isLoaded, hasError) and using this.IFRAME_TIMEOUT to mark timeouts.
   * @example
   * etupIframeLoadTracking()
   * // returns void
   * @returns {{void}} Does not return a value; updates camera state and iframe event handlers as side effects.
   */
  setupIframeLoadTracking(): void {
    const iframes = document.querySelectorAll('.stream-iframe');

    if (iframes.length === 0) {
      return;
    }

    iframes.forEach((iframe, index) => {
      if (index >= this.cameras.length) return;

      const iframeElement = iframe as HTMLIFrameElement;
      const camera = this.cameras[index];

      // Set timeout for each iframe (reduced to 15 seconds)
      const timeoutId = setTimeout(() => {
        if (camera.isLoading && !camera.isLoaded) {
          camera.isLoading = false;
          camera.hasError = true;
        }
      }, this.IFRAME_TIMEOUT);

      // Track when iframe loads successfully
      iframeElement.onload = () => {
        clearTimeout(timeoutId);
        if (camera.isLoading) {
          camera.isLoading = false;
          camera.isLoaded = true;
          camera.hasError = false;
        }
      };

      // Handle iframe errors
      iframeElement.onerror = () => {
        clearTimeout(timeoutId);
        if (camera.isLoading) {
          camera.isLoading = false;
          camera.hasError = true;
        }
      };
    });
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
